/**
 * Per-lesson settings for the AI Role Play material.
 *
 * These travel to the classroom as an untyped `settings` object so that adding one is a
 * change here and nowhere else — no schema edit in either gateway, no republish of the
 * query package. The trade is that this file is the only thing keeping the shape honest,
 * hence the explicit map rather than passing the form model through.
 */

export const AI_ROLE_PLAY_MODE = {
  TEXT: 'TEXT',
  VOICE: 'VOICE',
  TEXT_VOICE: 'TEXT_VOICE',
};

/** The module's own vocabulary; the classroom passes these straight through. */
const FEATURE = {
  TEXT: 'text',
  VOICE: 'voice',
};

const MODE_FEATURES = {
  [AI_ROLE_PLAY_MODE.TEXT]: [FEATURE.TEXT],
  [AI_ROLE_PLAY_MODE.VOICE]: [FEATURE.VOICE],
  [AI_ROLE_PLAY_MODE.TEXT_VOICE]: [FEATURE.TEXT, FEATURE.VOICE],
};

export const modeOptions = [
  { key: AI_ROLE_PLAY_MODE.TEXT, value: AI_ROLE_PLAY_MODE.TEXT, text: 'Text only' },
  { key: AI_ROLE_PLAY_MODE.VOICE, value: AI_ROLE_PLAY_MODE.VOICE, text: 'Voice only' },
  {
    key: AI_ROLE_PLAY_MODE.TEXT_VOICE,
    value: AI_ROLE_PLAY_MODE.TEXT_VOICE,
    text: 'Text and voice',
  },
];

/**
 * Text-only unless the lesson opts into voice, matching the classroom's own default.
 * Voice needs microphone permission and costs more per session, so a lesson that has
 * never been saved with a mode should not get it by implication.
 */
export const DEFAULT_MODE = AI_ROLE_PLAY_MODE.TEXT;

const KNOWN_FEATURES = Object.values(FEATURE);

/**
 * Order-insensitive, duplicate-tolerant identity for a feature list, so a stored
 * ['voice','text'] reads back as the same mode as ['text','voice'].
 */
const featureKey = (features) =>
  KNOWN_FEATURES.filter((feature) => features.includes(feature)).join(',');

const MODE_BY_FEATURE_KEY = Object.keys(MODE_FEATURES).reduce(
  (acc, mode) => ({ ...acc, [featureKey(MODE_FEATURES[mode])]: mode }),
  {}
);

/**
 * Derived from MODE_FEATURES rather than hand-written conditions, so a fourth mode is one
 * entry in that table and nothing else. Anything unrecognised — an unknown feature, an
 * empty list, a non-array — falls back to the default rather than guessing.
 */
export const enabledFeaturesToMode = (enabledFeatures) => {
  if (!Array.isArray(enabledFeatures)) return DEFAULT_MODE;
  return MODE_BY_FEATURE_KEY[featureKey(enabledFeatures)] || DEFAULT_MODE;
};

export const modeToEnabledFeatures = (mode) => MODE_FEATURES[mode] || MODE_FEATURES[DEFAULT_MODE];

/**
 * Read the form's view of a stored content object.
 *
 * Only the keys this editor renders — everything else is left in `content` and put back
 * untouched by `toContent`.
 */
export const toFormValue = ({ title, description, content = {} }) => ({
  title,
  description,
  scenarioId: content.scenarioId,
  mode: enabledFeaturesToMode((content.settings || {}).enabledFeatures),
});

/**
 * Write the form's fields back over the *stored* content, not over form state.
 *
 * FormGenerator's change handler returns `form.getModel()`, which holds registered inputs
 * only, so anything rebuilt from form state has already dropped the keys this build does
 * not render. Merging from `content` is what lets a newer setting survive a save made by
 * an older deploy of this page — including a save that only touched the title.
 *
 * Two levels of explicit shallow spread, deliberately not lodash merge: that merges
 * arrays by index, so a stored ['text','voice'] overlaid with ['text'] would come back
 * as ['text','voice'].
 */
export const toContent = (content = {}, formValue = {}) => ({
  ...content,
  scenarioId: formValue.scenarioId,
  settings: {
    ...(content.settings || {}),
    enabledFeatures: modeToEnabledFeatures(formValue.mode),
  },
});
